/**
 * Footer Component for WoodAlps Website
 * 
 * Comprehensive site footer providing navigation, contact information,
 * legal links, and brand elements. Integrates with Sanity CMS for
 * dynamic content and maintains consistent branding across the site.
 * 
 * Content Sections:
 * - Brand logo and social media links
 * - Product navigation (MTB, Road, Gravel)
 * - Company information and support
 * - Legal pages and documentation
 * - Contact information and location
 * 
 * Features:
 * - Responsive grid layout (1-2-5 column progression)
 * - Dynamic logo integration from Sanity CMS
 * - Social media integration with proper accessibility
 * - External link security (noopener noreferrer)
 * - Hover animations and visual feedback
 * - Mobile-optimized layout and spacing
 * 
 * Legal & Compliance:
 * - GDPR-compliant links to legal documents
 * - Terms of service and privacy policy
 * - Cookie policy and data handling
 * - Return policy and warranty information
 * 
 * Contact Information:
 * - Physical address for business transparency
 * - Contact email for customer support
 * - Social media presence for community engagement
 * 
 * Accessibility:
 * - Semantic HTML structure with proper headings
 * - Sufficient color contrast for readability
 * - Keyboard navigation support
 * - Screen reader compatible link descriptions
 * 
 * @param {object} siteSettings - Sanity CMS site settings
 * @param {object} siteSettings.logo - Logo configuration
 * @param {number} siteSettings.footerLogoWidth - Footer logo width
 * @param {number} siteSettings.footerLogoHeight - Footer logo height
 * 
 * @description Comprehensive site footer with navigation, legal links, and contact information
 */

import Link from 'next/link';
import Image from 'next/image';
import { Instagram, Facebook, Mail, MapPin } from 'lucide-react';
import { client } from '../../src/app/studio/lib/sanity';
import { urlForImage } from '../../src/app/studio/lib/image';
import { sanityLoader } from '../../utils/sanityLoader';
import { useTranslation } from '../../src/hooks/useTranslation';

const Footer = ({ siteSettings = null }) => {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();

  // Get logo details from site settings or use defaults
  const logoSrc = siteSettings?.logo ? urlForImage(siteSettings.logo) : "/images/logoWA.png";
  const logoWidth = siteSettings?.footerLogoWidth || 135;
  const logoHeight = siteSettings?.footerLogoHeight || 70;
  const logoAlt = siteSettings?.logo?.alt || "WoodAlps";

  return (
    <footer className="bg-deep-black text-soft-white pt-16 pb-8">
      <div className="container-custom">
        {/* Footer Top - Main Sections */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 mb-12">
          {/* Logo & Social Media */}
          <div className="flex flex-col">
            <Link href="/" className="block mb-8">
              <Image
                src={logoSrc}
                alt={logoAlt}
                loader={sanityLoader}
                width={logoWidth}
                height={logoHeight}
                className="object-contain"
              />
            </Link>
            <div className="flex space-x-6 mt-4">
              <a 
                href="https://facebook.com/woodalps" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-accent-color transition-colors"
              >
                <Facebook size={30} strokeWidth={1.5} />
              </a>
              <a 
                href="https://instagram.com/woodalps_wooden_bikes" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-accent-color transition-colors"
              >
                <Instagram size={30} strokeWidth={1.5} />
              </a>
            </div>
          </div>

          {/* Quick Links - Column 1 */}
          <div>
            <h4 className="text-lg font-medium mb-6">{t('footer.quick_links')}</h4>
            <ul className="space-y-3">
              <li>
                <Link href="/" className="text-gray-400 hover:text-accent-color transition-colors">
                  {t('footer.home')}
                </Link>
              </li>
              <li>
                <Link href="/road" className="text-gray-400 hover:text-accent-color transition-colors">
                  {t('navigation.road')}
                </Link>
              </li>
              <li>
                <Link href="/gravel" className="text-gray-400 hover:text-accent-color transition-colors">
                  {t('navigation.gravel')}
                </Link>
              </li>
              <li>
                <Link href="/mtb" className="text-gray-400 hover:text-accent-color transition-colors">
                  {t('navigation.mtb')}
                </Link>
              </li>
              <li>
                <Link href="/blog" className="text-gray-400 hover:text-accent-color transition-colors">
                  {t('navigation.blog')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Quick Links - Column 2 (WoodAlps World and Support) */}
          <div className="mt-0 md:mt-[52px]"> {/* Align with the first Quick Links column */}
            <ul className="space-y-3">
              {/* WoodAlps World and sublinks */}
              <li>
                <span className="text-gray-400">{t('footer.explore_us')}</span>
              </li>
              <li className="ml-3">
                <Link href="/about" className="text-gray-400 hover:text-accent-color transition-colors">
                  {t('footer.about_us')}
                </Link>
              </li>
              <li className="ml-3">
                <Link href="/ambassadors" className="text-gray-400 hover:text-accent-color transition-colors">
                  {t('footer.ambassadors')}
                </Link>
              </li>
              <li className="ml-3">
                <Link href="/design-and-engineering" className="text-gray-400 hover:text-accent-color transition-colors">
                  {t('footer.design_engineering')}
                </Link>
              </li>
              <li className="ml-3">
                <Link href="/gallery" className="text-gray-400 hover:text-accent-color transition-colors">
                  {t('navigation.gallery')}
                </Link>
              </li>
              {/* Support and sublinks */}
              <li>
                <span className="text-gray-400">{t('navigation.support')}</span>
              </li>
              <li className="ml-3">
                <Link href="/faq" className="text-gray-400 hover:text-accent-color transition-colors">
                  {t('navigation.faq')}
                </Link>
              </li>
              <li className="ml-3">
                <Link href="/contact" className="text-gray-400 hover:text-accent-color transition-colors">
                  {t('navigation.contact_us')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="text-lg font-medium mb-6">{t('footer.legal')}</h4>
            <ul className="space-y-3">
              <li>
                <Link href="/terms" className="text-gray-400 hover:text-accent-color transition-colors">
                  {t('footer.term_of_sales')}
                </Link>
              </li>
              <li>
                <Link href="/legal" className="text-gray-400 hover:text-accent-color transition-colors">
                  {t('footer.legal_notices')}
                </Link>
              </li>
              <li>
                <Link href="/delivery" className="text-gray-400 hover:text-accent-color transition-colors">
                  {t('footer.delivery_information')}
                </Link>
              </li>
              <li>
                <Link href="/ordering" className="text-gray-400 hover:text-accent-color transition-colors">
                  {t('footer.ordering_process')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-medium mb-6">{t('footer.contact_us_title')}</h4>
            <ul className="space-y-4">
              <li className="flex items-start">
                <MapPin size={20} className="text-accent-color mr-3 mt-1" />
                <span className="text-gray-400">
                  16 Route du Champ Poirier,<br />
                  74500 Lugrin, France
                </span>
              </li>
              <li className="flex items-center">
                <Mail size={20} className="text-accent-color mr-3" />
                <span className="text-gray-400">contact@woodalps.com</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-gray-800 py-6 mt-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-500 text-sm text-center md:text-left">
              &copy; {currentYear} WoodAlps. {t('footer.all_rights_reserved')}.
            </p>

          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer; 